import { useMemo } from "react";
import { useRawAddinData } from "./useAddinData";
import { useUpdateAddIn } from "./useUpdateAddIn";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { DeviceHistoryEntry } from "@/models/DeviceHistoryEntry";

export function useDeviceHistory(deviceId: string) {
  const { data: rawData = [], ...rest } = useRawAddinData<DeviceHistoryEntry>(
    AddInIds.deviceHistory,
  );

  const entries = useMemo(
    () =>
      rawData
        .map(d => d.details)
        .filter(e => e.deviceId === deviceId)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [rawData, deviceId],
  );

  return { data: entries, ...rest };
}

export function useLatestHistoryByDevice(): Record<string, DeviceHistoryEntry> {
  const { data: rawData = [] } = useRawAddinData<DeviceHistoryEntry>(AddInIds.deviceHistory);

  return useMemo(() => {
    const map: Record<string, DeviceHistoryEntry> = {};
    for (const { details } of rawData) {
      // Only consider remark entries (or legacy entries without a type)
      if (details.type === "statusChange") continue;
      // clearRemark entries update the latest entry (so remark shows as empty) but are not "visible"
      const existing = map[details.deviceId];
      if (!existing || new Date(details.createdAt) > new Date(existing.createdAt)) {
        map[details.deviceId] = details;
      }
    }
    return map;
  }, [rawData]);
}

export function useAddDeviceHistoryEntry() {
  const { username } = useGeotabClient();
  const mutation = useUpdateAddIn<DeviceHistoryEntry>(AddInIds.deviceHistory, "id");

  const addEntry = (deviceId: string, message: string) => {
    const entry: DeviceHistoryEntry = {
      id: `${deviceId}-${Date.now()}`,
      deviceId,
      message,
      createdBy: username,
      createdAt: new Date().toISOString(),
      type: "remark",
    };
    mutation.mutate(entry);
  };

  const addClearRemark = (deviceId: string) => {
    const entry: DeviceHistoryEntry = {
      id: `${deviceId}-${Date.now()}`,
      deviceId,
      message: "",
      createdBy: username,
      createdAt: new Date().toISOString(),
      type: "clearRemark",
    };
    mutation.mutate(entry);
  };

  const addStatusChange = (deviceId: string, message: string) => {
    const entry: DeviceHistoryEntry = {
      id: `${deviceId}-${Date.now()}`,
      deviceId,
      message,
      createdBy: username,
      createdAt: new Date().toISOString(),
      type: "statusChange",
    };
    mutation.mutate(entry);
  };

  return { addEntry, addClearRemark, addStatusChange, isPending: mutation.isPending };
}
